import { pageModified } from "@/data/pageDates";
import { SITE_URL, absoluteUrl, ORG_ID, SITE_ID, ADAM_ID, LOGO_ID } from "@/lib/site";
import { catalogueOfferIdsForPath } from "@/data/tiers";
import { deliveredThrough, type ExpertiseSlug } from "@/data/expertise";
import { pageKeywords } from "@/data/pageKeywords";
import { scanUrl } from "@/lib/diagnostic";

export { SITE_URL, absoluteUrl, ORG_ID, SITE_ID, ADAM_ID, LOGO_ID };

/** Canonical link entry for a route's head() */
export const canonical = (path: string) => ({ rel: "canonical", href: absoluteUrl(path) });

/**
 * Language/region alternates. The site is single-locale (Australian English),
 * so each page self-references as en-AU and is also the x-default.
 * Both point at the same URL as the canonical, so they never conflict.
 */
export const hreflang = (path: string) => [
  { rel: "alternate", hrefLang: "en-AU", href: absoluteUrl(path) },
  { rel: "alternate", hrefLang: "x-default", href: absoluteUrl(path) },
];

/** Canonical + hreflang alternates for a route's head() links array */
export const canonicalLinks = (path: string) => [canonical(path), ...hreflang(path)];

/** og:url meta entry for a route's head() */
export const ogUrl = (path: string) => ({ property: "og:url", content: absoluteUrl(path) });

export const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/** Absolute URL for a page-specific social card in /public/og */
export const ogImageUrl = (name: string) => `${SITE_URL}/og/${name}.jpg`;

/**
 * Per-page og:image / twitter:image meta entries (1200x630).
 * Spread into a route's head() meta array.
 */
export const ogImage = (name: string, alt: string) => [
  { property: "og:image", content: ogImageUrl(name) },
  { property: "og:image:secure_url", content: ogImageUrl(name) },
  { property: "og:image:type", content: "image/jpeg" },
  { property: "og:image:width", content: "1200" },
  { property: "og:image:height", content: "630" },
  { property: "og:image:alt", content: alt },
  { name: "twitter:image", content: ogImageUrl(name) },
  { name: "twitter:image:alt", content: alt },
];

/** JSON-LD script entry for a route's head() */
export const jsonLd = (data: object) => ({
  type: "application/ld+json",
  children: JSON.stringify(data),
});


/** One canonical description, reused byte-identically everywhere on and off site. */
export const ORG_DESCRIPTION =
  "Ajility provides senior, hands-on fractional COO leadership for founder-led Australian businesses generating $3M to $30M in annual revenue, installing operational structure, financial clarity and execution discipline.";

/**
 * The site-wide @graph, emitted from the root on every page.
 *
 * `path` is the current pathname. It gates nodes that reference a per-page
 * node, so nothing in the graph points at an @id that does not exist on the
 * page emitting it.
 */
const KNOWS_ABOUT = [
  "Business partnership and shareholder alignment",
  "Restructuring and performance management for owner-led businesses",
  "Pricing strategy and price architecture",
  "Revenue management",
  "Margin improvement and unit economics",
  "Revenue leakage, credits, rebates and discount control",
  "Business planning and annual operating budgets",
  "Rolling cash forecasting",
  "Operating and margin analysis",
  "Remuneration, bonus and commission design",
  "Role design, targets and accountability",
  "Fractional COO leadership",
  "Operating rhythm and governance design",
  "Strategy execution and 90-day planning",
  "Project and contract commercial control, cost to complete and variation capture",
  "Procurement and supplier cost reduction",
  "Business systems selection and implementation",
  "Project and initiative leadership",
  "Process improvement, process mapping and standard operating procedures",
  "Organisation design, accountability charts and role scorecards",
  "Fractional general manager (fractional GM) leadership for owner-led businesses",
];

/** Where Ajility works. Shared by the Organization node and every Service node. */
const AREA_SERVED = [
  { "@type": "City", name: "Sydney" },
  { "@type": "City", name: "Melbourne" },
  { "@type": "City", name: "Newcastle" },
  { "@type": "City", name: "Perth" },
  { "@type": "AdministrativeArea", name: "Central Coast" },
  { "@type": "Country", name: "Australia" },
];

/** Keywords field for a path, only where src/data/pageKeywords.ts lists one. */
const keywordsFor = (path: string) => (pageKeywords[path] ? { keywords: pageKeywords[path] } : {});

/** Free tool pages listed in the Organization's free tools catalogue (names match src/data/tools.ts). */
const FREE_TOOLS: [string, string][] = [
  ["cash-runway-calculator", "Cash Runway Calculator"],
  ["price-change-impact-calculator", "Price Change Impact Calculator"],
  ["time-and-accountability-grid", "Time and Accountability Grid"],
  ["fractional-coo-cost-calculator", "Fractional COO vs Full Time COO Cost Calculator"],
  ["revenue-leakage-check", "Revenue Leakage Self Check"],
  ["weekly-scorecard-builder", "Weekly Scorecard Builder"],
  ["partner-alignment-check", "Partner Alignment Check"],
];

const CATALOGUE_ID = `${SITE_URL}/packages#catalogue`;

export const siteGraph = (path?: string) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": ORG_ID,
      name: "Ajility",
      legalName: "AJILITY PTY LTD",
      url: `${SITE_URL}/`,
      description: ORG_DESCRIPTION,
      email: "adam@ajility.com.au",
      telephone: "+61 423 335 555",
      taxID: "39649782427",
      iso6523Code: "0151:39649782427",
      identifier: [
        { "@type": "PropertyValue", propertyID: "ABN", value: "39649782427" },
        { "@type": "PropertyValue", propertyID: "ACN", value: "649782427" },
      ],
      foundingDate: "2021-04-28",
      logo: { "@id": LOGO_ID },
      image: OG_IMAGE,
      employee: { "@id": ADAM_ID },
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "sales",
          email: "adam@ajility.com.au",
          telephone: "+61 423 335 555",
          areaServed: "AU",
          availableLanguage: "en-AU",
        },
      ],
      knowsAbout: KNOWS_ABOUT,
      areaServed: AREA_SERVED,
      address: {
        "@type": "PostalAddress",
        addressLocality: "Sydney",
        addressRegion: "NSW",
        addressCountry: "AU",
      },
      sameAs: [
        "https://www.linkedin.com/company/ajility",
        "https://abr.business.gov.au/ABN/View?abn=39649782427",
        "https://creditorwatch.com.au/credit/profile/649782427",
      ],
      // On /packages the full OfferCatalog node is declared by the page, so a
      // bare reference is correct. Elsewhere it is emitted as a minimal node.
      hasOfferCatalog: [
        path === "/packages"
          ? { "@id": CATALOGUE_ID }
          : {
              "@type": "OfferCatalog",
              "@id": CATALOGUE_ID,
              name: "Ajility service catalogue",
              url: `${SITE_URL}/packages`,
            },
        {
          "@type": "OfferCatalog",
          "@id": `${SITE_URL}/tools#free-tools`,
          name: "Ajility free tools",
          url: `${SITE_URL}/tools`,
          itemListElement: FREE_TOOLS.map(([slug, name]) => ({
            "@type": "SoftwareApplication",
            "@id": `${SITE_URL}/tools/${slug}#app`,
            name,
            url: `${SITE_URL}/tools/${slug}`,
          })),
        },
      ],
    },

    {
      "@type": "WebSite",
      "@id": SITE_ID,
      url: `${SITE_URL}/`,
      name: "Ajility",
      description: ORG_DESCRIPTION,
      publisher: { "@id": ORG_ID },
      inLanguage: "en-AU",
    },
    {
      "@type": "Person",
      "@id": ADAM_ID,
      name: "Adam Ayoub",
      givenName: "Adam",
      familyName: "Ayoub",
      jobTitle: "Fractional COO and Integrator",
      description:
        "Adam Ayoub is the Fractional COO and Integrator at Ajility, with more than fifteen years in general manager and chief operating officer roles across mining services, professional services, construction, logistics, retail and high-growth SMEs.",
      worksFor: { "@id": ORG_ID },
      url: `${SITE_URL}/about/adam-ayoub`,
      // Only emitted on /about/adam-ayoub, where the #webpage node exists.
      ...(path === "/about/adam-ayoub"
        ? { mainEntityOfPage: { "@id": `${SITE_URL}/about/adam-ayoub#webpage` } }
        : {}),
      image: {
        "@type": "ImageObject",
        url: `${SITE_URL}/images/adam-ayoub.jpg`,
        width: 800,
        height: 1000,
        caption: "Adam Ayoub, Fractional COO and Integrator, Ajility",
      },
      knowsAbout: KNOWS_ABOUT,
      knowsLanguage: "en-AU",
      nationality: { "@type": "Country", name: "Australia" },
      workLocation: [
        { "@type": "City", name: "Sydney" },
        { "@type": "City", name: "Newcastle" },
        { "@type": "City", name: "Melbourne" },
        { "@type": "City", name: "Perth" },
        { "@type": "AdministrativeArea", name: "Central Coast" },
      ],
      alumniOf: {
        "@type": "EducationalOrganization",
        name: "University of Western Sydney",
      },
      hasCredential: {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "degree",
        name: "Bachelor of Business (Accounting)",
      },
      hasOccupation: {
        "@type": "Occupation",
        name: "Chief Operating Officer",
        occupationalCategory: "11-1021.00",
        skills:
          "Pricing strategy, revenue management, business planning, budgeting, rolling cash forecasting, remuneration design and operating governance",
      },
      sameAs: ["https://www.linkedin.com/in/adamayoub/"],
    },

    {
      "@type": "ImageObject",
      "@id": LOGO_ID,
      url: `${SITE_URL}/logo-square-1000.png`,
      contentUrl: `${SITE_URL}/logo-square-1000.png`,
      width: 1000,
      height: 1000,
      caption: "Ajility",
    },
  ],
});

/** Per-page WebPage node. Carries the only dateModified signal on non-article pages. */
export const webPageSchema = (opts: {
  path: string;
  name: string;
  description: string;
  dateModified?: string;
  /** Set false on pages with no BreadcrumbList (the homepage). */
  breadcrumb?: boolean;
  type?: "WebPage" | "AboutPage" | "ContactPage" | "CollectionPage" | "ProfilePage";
  /** @id of the node this page is about (a Service or Article on the same page). */
  mainEntityId?: string;
  /** Name of the page's existing social card in /public/og, as passed to ogImage(). */
  image?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": opts.type ?? "WebPage",
  "@id": `${absoluteUrl(opts.path)}#webpage`,
  url: absoluteUrl(opts.path),
  name: opts.name,
  description: opts.description,
  isPartOf: { "@id": SITE_ID },
  ...(opts.type === "ProfilePage"
    ? { mainEntity: { "@id": ADAM_ID } }
    : opts.mainEntityId
      ? { mainEntity: { "@id": opts.mainEntityId }, about: { "@id": opts.mainEntityId } }
      : { about: { "@id": ORG_ID } }),
  publisher: { "@id": ORG_ID },
  ...(opts.image
    ? { primaryImageOfPage: { "@type": "ImageObject", url: ogImageUrl(opts.image) } }
    : {}),
  ...(opts.breadcrumb === false ? {} : { breadcrumb: { "@id": `${absoluteUrl(opts.path)}#breadcrumb` } }),
  dateModified: opts.dateModified ?? pageModified(opts.path),
  inLanguage: "en-AU",
  ...keywordsFor(opts.path),
});

/** Service page structured data */
export const serviceSchema = (opts: {
  name: string;
  description: string;
  path: string;
  slug: string;
  price?: number;
  /** Catalogue offer id(s) from src/data/tiers.ts, referenced rather than re-declared. */
  offerId?: string | string[];
  serviceType?: string;
}) => {
  /**
   * A product is declared as a full Offer node exactly once site-wide, in the
   * OfferCatalog generated from the tier data. Anything a service page would
   * otherwise declare is reduced to a bare @id reference to that node.
   *
   * Explicit offerIds win; otherwise the catalogue is resolved from the page
   * path, so new products are covered automatically. Only when the catalogue
   * declares nothing for this path does the page declare its own Offer node.
   */
  const explicit = opts.offerId
    ? (Array.isArray(opts.offerId) ? opts.offerId : [opts.offerId]).map(
        (id) => `${SITE_URL}/packages#offer-${id}`,
      )
    : [];

  const resolved = explicit.length ? explicit : catalogueOfferIdsForPath(opts.path);
  const offerIds = resolved.map((id) => ({ "@id": id }));

  // A specialisation is related to the service pages its own "Delivered through" line links to.
  const specialisation = opts.path.match(/^\/specialisations\/(.+)$/)?.[1] as ExpertiseSlug | undefined;
  const relatedServices = specialisation
    ? (deliveredThrough[specialisation] ?? [])
        .filter((d) => d.to.startsWith("/services/"))
        .map((d) => ({ "@id": `${absoluteUrl(d.to)}#service` }))
    : [];


  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${absoluteUrl(opts.path)}#service`,
    name: opts.name,
    description: opts.description,
    url: absoluteUrl(opts.path),
    serviceType: opts.serviceType ?? "Operations consulting",
    category: "Fractional COO",
    provider: { "@id": ORG_ID },
    areaServed: AREA_SERVED,
    termsOfService: absoluteUrl("/terms"),
    ...(relatedServices.length ? { isRelatedTo: relatedServices } : {}),
    audience: {
      "@type": "BusinessAudience",
      name: "Founder-led Australian businesses generating $3M to $30M in annual revenue",
    },
    ...(offerIds.length
      ? { offers: offerIds.length === 1 ? offerIds[0] : offerIds }
      : opts.price
        ? {
            offers: {
              "@type": "Offer",
              "@id": `${absoluteUrl(opts.path)}#offer`,
              priceSpecification: {
                "@type": "PriceSpecification",
                minPrice: String(opts.price),
                priceCurrency: "AUD",
                valueAddedTaxIncluded: false,
              },
              availability: "https://schema.org/InStock",
              url: absoluteUrl("/packages"),
              seller: { "@id": ORG_ID },
            },
          }
        : {}),
  };
};


/** Insight article structured data */
export const articleSchema = (opts: {
  title: string;
  description: string;
  slug: string;
  datePublished: string;
  dateModified?: string;
  section?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "@id": `${absoluteUrl(`/insights/${opts.slug}`)}#article`,
  headline: opts.title,
  description: opts.description,
  url: absoluteUrl(`/insights/${opts.slug}`),
  mainEntityOfPage: { "@id": `${absoluteUrl(`/insights/${opts.slug}`)}#webpage` },
  datePublished: opts.datePublished,
  dateModified: opts.dateModified ?? opts.datePublished,
  ...(opts.section ? { articleSection: opts.section } : {}),
  inLanguage: "en-AU",
  ...keywordsFor(`/insights/${opts.slug}`),
  isAccessibleForFree: true,
  author: { "@id": ADAM_ID },
  publisher: { "@id": ORG_ID },
  isPartOf: { "@id": SITE_ID },
  image: {
    "@type": "ImageObject",
    url: ogImageUrl(`insights-${opts.slug}`),
    width: 1200,
    height: 630,
  },
});

/** Blog listing structured data for /insights */
export const blogSchema = (
  items: { slug: string; title: string; description: string; datePublished: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  "@id": `${SITE_URL}/insights#blog`,
  name: "Ajility Insights",
  description:
    "Operating insights on scaling founder-led businesses: execution rhythm, margin visibility, structure and fractional COO leadership.",
  url: absoluteUrl("/insights"),
  inLanguage: "en-AU",
  publisher: { "@id": ORG_ID },
  blogPost: items.map((item) => ({
    "@type": "Article",
    "@id": `${SITE_URL}/insights/${item.slug}#article`,
    headline: item.title,
    description: item.description,
    url: absoluteUrl(`/insights/${item.slug}`),
    datePublished: item.datePublished,
    author: { "@id": ADAM_ID },
  })),
});

/**
 * BreadcrumbList structured data. Home is prepended automatically.
 * Pass the trail from the top-level section down to the current page.
 */
export const breadcrumbSchema = (trail: { name: string; path: string }[], path?: string) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${absoluteUrl(path ?? trail[trail.length - 1]?.path ?? "/")}#breadcrumb`,
  itemListElement: [{ name: "Home", path: "/" }, ...trail].map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

/** FAQPage node. Answers must also appear as plain text in the visible HTML. */
export const faqPageSchema = (
  path: string,
  faqs: { q: string; a: string }[],
  /** Defaults to the organisation; pass the page's mainEntity @id where it has one. */
  aboutId: string = ORG_ID,
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${absoluteUrl(path)}#faq`,
  isPartOf: { "@id": SITE_ID },
  about: { "@id": aboutId },
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const SCAN_APP_ID = `${SITE_URL}/scan#app`;

/** The free Operating Scan as a web application. Emitted on /scan, /tools and the scan article. */
export const scanAppSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "@id": SCAN_APP_ID,
  name: "Operating Scan",
  alternateName: ["Free business operations health check", "Free operations health check for Australian businesses"],
  description:
    "A free 21-statement operations health check for founder-led Australian businesses. About six minutes, with an immediate provisional score across Direction, Leadership, People, Rhythm, Numbers and Systems.",
  url: scanUrl("scan_page"),
  applicationCategory: "BusinessApplication",
  operatingSystem: "Any web browser",
  isAccessibleForFree: true,
  inLanguage: "en-AU",
  provider: { "@id": ORG_ID },
  offers: { "@id": `${SITE_URL}/packages#offer-operating-scan` },
});
